/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoHowTos, IoHowToSteps } from '@cloudio-saas/datasource-types';
import { Divider } from '@mui/material';
import { Box, IconButton } from '@mui/material';
import { toText } from 'components/RichTextEditor/RichTextEditor';
import { isSameHour, parseISO } from 'date-fns';
import Icon from 'icons/Icon';
import TimeAgo from 'react-timeago';
import Store from 'store/Store';
import { useStoreDirty } from 'store/useAppStore';
import { recordId } from 'store/utils/utils';
import { formatSeconds } from 'utils/dateUtils';
import { isEmpty } from 'utils/isEmpty';

export default function HowToInfo({
  followOn = false,
  howTo,
  howToStore,
  stepsStore,
}: {
  followOn?: boolean;
  howTo: DBRow<IoHowTos>;
  howToStore?: Store<IoHowTos>;
  stepsStore?: Store<IoHowToSteps>;
}) {
  const dirty = useStoreDirty(howToStore);
  const stepsDirty = useStoreDirty(stepsStore);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        height: '32px',
      }}
    >
      {howTo.totalSteps + (followOn ? 0 : 1)} steps
      <Divider orientation="vertical" variant="middle" />
      {formatSeconds(howTo.totalSeconds ?? 0)}
      <Divider orientation="vertical" variant="middle" />
      <TimeAgo
        date={howTo.lastUpdateDate ?? ''}
        data-tip={
          isSameHour(
            parseISO(howTo.lastUpdateDate),
            parseISO(howTo.creationDate),
          )
            ? 'Created On'
            : 'Updated On'
        }
      />
      {!followOn ? (
        <>
          <Divider orientation="vertical" variant="middle" />
          <a
            href={
              howToStore
                ? `#/${howTo.appUid}/how-to`
                : `#/how-to/${howTo.appUid}`
            }
            data-tip="Home"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon iconName="home" />
          </a>
        </>
      ) : null}
      {howToStore ? (
        <>
          <Divider orientation="vertical" variant="middle" />
          <a
            href={`#/how-to/${howTo.appUid}/${howTo.howToUid}`}
            target="_blank"
            rel="noreferrer"
            data-tip="Preview in new tab"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon iconName="eye" />
          </a>
          {dirty || stepsDirty ? (
            <>
              <Divider orientation="vertical" variant="middle" />
              {dirty ? (
                <IconButton
                  color="primary"
                  onClick={async () => {
                    howToStore.reset();
                  }}
                  data-tip="Revert Changes to this header"
                >
                  <Icon iconName="undo" color="primary" />
                </IconButton>
              ) : null}
              <IconButton
                onClick={async () => {
                  if (dirty) {
                    if (howTo.howToDescription) {
                      const desc = toText(howTo.howToDescription);
                      if (isEmpty(desc)) {
                        howToStore.updateRecord(recordId(howTo), {
                          howToDescription: undefined,
                        });
                      }
                    }
                    await howToStore.save({ feedback: 'NONE', silent: true });
                  }
                  if (stepsStore && stepsDirty) {
                    await stepsStore.save({ feedback: 'NONE', silent: true });
                  }
                }}
                data-tip="Save Changes"
              >
                <Icon iconName="save" color="primary" />
              </IconButton>
            </>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
